import React, { createContext, useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode"; // Corrected import for jwt-decode
import Cookies from "js-cookie"; // Import the js-cookie library
import API from "../services/api";
import { toast } from "react-toastify";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // To show loading until we verify the token

  //************************************************************* */
  const [products, setProducts] = useState([]);
  const [series, setSeries] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  const [seriesLoading, setSeriesLoading] = useState(false);
  const [productsError, setProductsError] = useState(null);
  const [seriesError, setSeriesError] = useState(null);
  // Pagination meta for the current getProducts() page
  const [productsMeta, setProductsMeta] = useState({
    page: 1,
    pages: 1,
    total: 0,
    limit: 24,
    hasNextPage: false,
    hasPrevPage: false,
  });

  // Added state for business details and errors
  const [businessDetails, setBusinessDetails] = useState([]); // <-- Add this
  const [errorBusinessDetails, setBusinessDetailsError] = useState(null); // <-- Add this
  const [BusinessDetailsLoading, setBusinessDetailsLoading] = useState(false); // Add this line
  const [businessDetailsStats, setBusinessDetailsStats] = useState({
    // Add stats
    totalCount: 0,
    userRole: null,
    page: 1,
    pages: 1,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const [quotations, setQuotations] = useState([]); // Add this line
  const [quotationsLoading, setQuotationsLoading] = useState(false); // Add this line
  const [quotationsError, setQuotationsError] = useState(null); // Add this line
  const [quotationsStats, setQuotationsStats] = useState({
    // Add quotation stats
    totalCount: 0,
    userRole: null,
    page: 1,
    pages: 1,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });

  // Check for token and user data in cookies when the component mounts
  useEffect(() => {
    const token = Cookies.get("token");

    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000; // Current time in seconds

        // Check if token is expired
        if (decodedToken.exp < currentTime) {
          logout(); // Token is expired, log out the user
        } else {
          setUser(decodedToken); // Set user info from the decoded token
        }
      } catch (error) {
        console.error("Invalid token", error);
        logout(); // Logout in case of invalid token
      }
    }

    setLoading(false); // Set loading to false after the check is complete
  }, []);

  const login = (token) => {
    Cookies.set("token", token, { expires: 7 }); // Set the token in cookies with an expiration of 7 days

    try {
      const decodedToken = jwtDecode(token);
      console.log("Decoded Token:", decodedToken);
      setUser(decodedToken);
      console.log(decodedToken);
    } catch (error) {
      console.error("Invalid token provided during login", error);
    }
  };

  const logout = () => {
    Cookies.remove("token"); // Remove the token from cookies
    setUser(null);
  };

  //******************************************************/
  // Paginated by default (page/limit/keyword/series/sort). Call with no
  // arguments to get page 1 at the default page size.
  const getProducts = async (params = {}) => {
    setProductsLoading(true);
    setProductsError(null); // Reset error state
    try {
      const { page = 1, limit = productsMeta.limit || 24, keyword, series: seriesFilter, sort } = params;
      const response = await API.get("/api/admin/product", {
        params: { page, limit, keyword, series: seriesFilter, sort },
      });
      const data = response.data;

      if (data && Array.isArray(data.products)) {
        setProducts(data.products);
        setProductsMeta({
          page: data.page,
          pages: data.pages,
          total: data.total,
          limit: data.limit,
          hasNextPage: data.hasNextPage,
          hasPrevPage: data.hasPrevPage,
        });
      } else {
        // Backward-compat: plain array response
        setProducts(data || []);
      }
      console.log("Products Data: ", data);
    } catch (errorMessage) {
      const error =
        errorMessage.response?.data?.message ||
        errorMessage.message ||
        "Failed to load products";
      setProductsError(error); // Update error state
      toast.error(error, {
        autoClose: 3000,
        hideProgressBar: true,
        theme: "dark",
      });
    } finally {
      setProductsLoading(false); // Reset loading state
    }
  };

  const getSeries = async () => {
    setSeriesLoading(true);
    setSeriesError(null); // Reset error state
    try {
      const response = await API.get("/api/admin/product/series");
      const seriesData = response.data || [];
      setSeries(seriesData);
    } catch (errorMessage) {
      const error =
        errorMessage.response?.data?.message ||
        errorMessage.message ||
        "Failed to load series";
      setSeriesError(error); // Update error state
      toast.error(error, {
        autoClose: 3000,
        hideProgressBar: true,
        theme: "dark",
      });
    } finally {
      setSeriesLoading(false); // Reset loading state
    }
  };

  //***************************************************************** */
  // UPDATED: Handle new response structure from backend
  // Accepts { page, limit, keyword } to paginate/search server-side.
  const getBusinessInfo = async (params = {}) => {
    try {
      setBusinessDetailsLoading(true);
      setBusinessDetailsError(null);

      const { page = 1, limit = businessDetailsStats.limit || 10, keyword } = params;
      const response = await API.get(`/api/admin/business-info`, {
        params: { page, limit, keyword },
      });

      // Handle the new response structure
      if (response.data && typeof response.data === "object") {
        // New structure: { businessInfo: [...], totalCount: 10, userRole: 'admin' }
        if (response.data.businessInfo) {
          setBusinessDetails(response.data.businessInfo);
          setBusinessDetailsStats({
            totalCount:
              response.data.totalCount || response.data.businessInfo.length,
            userRole: response.data.userRole || user?.role || null,
            page: response.data.page || page,
            pages: response.data.pages || 1,
            limit: response.data.limit || limit,
            hasNextPage: !!response.data.hasNextPage,
            hasPrevPage: !!response.data.hasPrevPage,
          });
        } else {
          // Fallback: if it's still the old array structure
          setBusinessDetails(response.data);
          setBusinessDetailsStats({
            totalCount: response.data.length,
            userRole: user?.role || null,
          });
        }
      } else {
        // Handle unexpected response format
        console.warn("Unexpected response format:", response.data);
        setBusinessDetails([]);
        setBusinessDetailsStats({
          totalCount: 0,
          userRole: user?.role || null,
        });
      }

      console.log("Business Info Data: ", response.data);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Failed to load business information";
      setBusinessDetailsError(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        style: {
          background: "#fee2e2",
          color: "#b91c1c",
          padding: "16px",
          borderRadius: "8px",
          border: "2px solid #fecaca",
        },
        icon: "❌",
      });
    } finally {
      setBusinessDetailsLoading(false);
    }
  };

  // UPDATED: Handle new quotations response structure from backend
  // Accepts { page, limit, keyword, status } to paginate/search/filter server-side.
  const getQuotations = async (params = {}) => {
    try {
      setQuotationsLoading(true);
      setQuotationsError(null);

      const { page = 1, limit = quotationsStats.limit || 10, keyword, status } = params;
      const response = await API.get("/api/admin/quotations", {
        params: { page, limit, keyword, status },
      });

      // Handle the new response structure
      if (response.data && typeof response.data === "object") {

        if (response.data.quotations) {
          const sortedQuotations = response.data.quotations.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          setQuotations(sortedQuotations);
          setQuotationsStats({
            totalCount: response.data.totalCount || sortedQuotations.length,
            userRole: response.data.userRole || user?.role || null,
            page: response.data.page || page,
            pages: response.data.pages || 1,
            limit: response.data.limit || limit,
            hasNextPage: !!response.data.hasNextPage,
            hasPrevPage: !!response.data.hasPrevPage,
          });
        } else {
          // Fallback: if it's still the old array structure
          const sortedQuotations = response.data.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          setQuotations(sortedQuotations);
          setQuotationsStats({
            totalCount: sortedQuotations.length,
            userRole: user?.role || null,
          });
        }
      } else {
        // Handle unexpected response format
        console.warn("Unexpected quotations response format:", response.data);
        setQuotations([]);
        setQuotationsStats({ totalCount: 0, userRole: user?.role || null });
      }

      console.log("Quotations Data: ", response.data);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        "An error occurred while fetching data. Please try again later.";
      setQuotationsError(errorMessage);
      console.error("Error fetching quotations:", err);
    } finally {
      setQuotationsLoading(false);
    }
  };

  // NEW: Get user's PDF settings (bank details and header image)
  const getUserPDFSettings = async (userId) => {
    try {
      const response = await API.get(`/api/users/${userId}/pdf-settings`);
      return response.data;
    } catch (error) {
      console.error("Error fetching PDF settings:", error);
      return null;
    }
  };

  // NEW: Get full user profile with all details
  const getUserProfile = async (userId) => {
    try {
      const response = await API.get(`/api/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching user profile:", error);
      return null;
    }
  };

  // NEW: Update user profile
  const updateUserProfile = async (userId, profileData) => {
    try {
      const response = await API.put(`/api/users/${userId}`, profileData);
      return response.data;
    } catch (error) {
      console.error("Error updating user profile:", error);
      throw error;
    }
  };

  if (loading) {
    return <div>Loading...</div>; // Render a loading state until token is checked
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        getProducts,
        products,
        setProducts,
        productsError,
        productsLoading,
        productsMeta,

        getSeries,
        series,
        setSeries,
        seriesError,
        seriesLoading,

        getBusinessInfo,
        businessDetails,
        setBusinessDetails,
        errorBusinessDetails,
        BusinessDetailsLoading,
        businessDetailsStats, // Add this to provide access to stats

        getQuotations,
        quotations,
        setQuotations,
        quotationsError,
        quotationsLoading,
        quotationsStats, // Add this to provide access to quotation stats

        // NEW: PDF and profile functions
        getUserPDFSettings,
        getUserProfile,
        updateUserProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
